import { type ChatHistory, ChatTranscript } from "@/components/analysis/chat-transcript";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Link, Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useEffect, useState } from "react";

/**
 * `/analysis/discord/$channelId` — admin-only full transcript of a watched
 * Discord channel, rendered by the shared `ChatTranscript` (messages
 * interleaved with what each analysis run created).
 *
 * E2E behaviour contract lives in `e2e/analysis-discord.spec.ts`.
 */

export const Route = createFileRoute("/_app/analysis/discord/$channelId")({
  component: DiscordChannelHistoryPage,
});

function DiscordChannelHistoryPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const { channelId } = Route.useParams();
  const [data, setData] = useState<ChatHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    let alive = true;
    api
      .get<ChatHistory>(
        `/api/auto-sources/watched-discord-channels/${encodeURIComponent(channelId)}/messages`,
      )
      .then((res) => {
        if (alive) setData(res);
      })
      .catch(() => {
        if (alive) setError("Couldn't load this channel.");
      });
    return () => {
      alive = false;
    };
  }, [channelId, user.globalRole]);

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{data?.chat.name ?? "Channel"}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Full transcript, with what each analysis run created shown inline.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/agents/$slug/$tab" params={{ slug: "analyst", tab: "discord" }}>
            Back
          </Link>
        </Button>
      </div>

      <ChatTranscript
        data={data}
        error={error}
        mediaUrl={(id) => `/api/auto-sources/discord-media/${id}`}
      />
    </div>
  );
}
